/**
 * Chatbot Analytics Tracker
 * Main entry point for the tracking SDK
 */

import { TrackerConfig, TrackingEvent, EventType, ProductData, CartData, OrderData } from './types';
import { SessionManager } from './session-manager';
import { ApiClient } from './api-client';
import { PlatformDetector } from './adapters/detector';
import { BaseAdapter } from './adapters/base-adapter';
import { Fingerprint } from './utils/fingerprint';
import { DebugOverlay } from './utils/debug-overlay';
import { BotDetector } from './utils/bot-detector';

export class ChatbotAnalyticsTracker {
  private config!: TrackerConfig;
  private sessionManager!: SessionManager;
  private apiClient!: ApiClient;
  private adapter!: BaseAdapter;
  private debugOverlay: DebugOverlay | null = null;
  private initialized: boolean = false;

  /**
   * Initialize the tracker
   */
  init(config: Partial<TrackerConfig>): void {
    if (this.initialized) {
      return;
    }

    // Merge with defaults
    this.config = {
      clientId: config.clientId || '',
      apiEndpoint: config.apiEndpoint || 'https://analytics.yourdomain.com/api/track',
      debug: config.debug || false,
      batchSize: config.batchSize || 10,
      batchInterval: config.batchInterval || 5000,
      sessionTimeout: config.sessionTimeout || 30,
      respectDoNotTrack: config.respectDoNotTrack !== false,
      platform: config.platform
    };

    if (!this.config.clientId) {
      throw new Error('clientId is required');
    }

    // --- Bot & DNT filtering (early exit) ---
    // Allow bypass for testing: ?chatbot_force_track in URL skips bot detection
    const forceTrack = new URLSearchParams(window.location.search).has('chatbot_force_track');
    
    if (!forceTrack) {
      // Respect Do Not Track if configured (default: true)
      if (this.config.respectDoNotTrack && Fingerprint.isDoNotTrackEnabled()) {
        return;
      }

      // Bot detection
      const botResult = BotDetector.detect();
      if (botResult.isBot) {
        return;
      }
    }

    this.log('Initializing Chatbot Analytics', this.config);

    // Initialize components
    this.sessionManager = new SessionManager(
      this.config.sessionTimeout!,
      this.config.debug!
    );

    this.apiClient = new ApiClient(
      this.config.apiEndpoint!,
      this.config.clientId!,
      this.config.batchSize!,
      this.config.batchInterval!,
      this.config.debug!
    );

    // Detect platform
    const detector = new PlatformDetector(this.config.debug);
    this.adapter = this.config.platform 
      ? detector.force(this.config.platform)
      : detector.detect();

    this.adapter.init();

    // Initialize debug overlay if debug mode or URL param
    if (this.config.debug || this.hasDebugParam()) {
      this.debugOverlay = new DebugOverlay();
      this.debugOverlay.init();
      this.debugOverlay.updateSessionInfo(this.sessionManager.getSession());
    }

    // Setup platform hooks
    this.setupHooks();

    // Setup chatbot product click tracking
    this.setupChatbotProductClickTracking();

    // Track page view
    this.trackPageView();

    this.initialized = true;
    this.log('✅ Chatbot Analytics initialized');
  }

  /**
   * Setup platform-specific hooks
   */
  private setupHooks(): void {
    // Hook: Product View
    this.adapter.hookProductView((product) => {
      this.trackProductView(product);
    });

    // Hook: Add to Cart
    this.adapter.hookAddToCart((product) => {
      this.trackAddToCart(product);
    });

    // Hook: View Cart
    this.adapter.hookViewCart((cart) => {
      this.trackViewCart(cart);
    });

    // Hook: Checkout
    this.adapter.hookCheckout((cart) => {
      this.trackCheckout(cart);
    });

    // Hook: Purchase
    this.adapter.hookPurchase((order) => {
      this.trackPurchase(order);
    });
  }

  /**
   * Setup chatbot product click tracking
   * Detects clicks on product links within chatbot widget
   */
  private setupChatbotProductClickTracking(): void {
    const setupClickHandler = (root: Document | ShadowRoot) => {
      root.addEventListener('click', (e: Event) => {
        const mouseEvent = e as MouseEvent;
        let target = mouseEvent.target as HTMLElement;
        
        // Traverse up to find the actual link/button (in case user clicked on text inside the link)
        let clickedElement: HTMLElement | null = null;
        let maxDepth = 5;
        
        while (target && maxDepth > 0) {
          // Check if this element is a product link or button
          if (target.classList && (
            target.classList.contains('viewDetails') || 
            target.classList.contains('viewDetailsLinkTitle') ||
            target.classList.contains('add_to_cart_button')
          )) {
            clickedElement = target;
            break;
          }
          target = target.parentElement as HTMLElement;
          maxDepth--;
        }
        
        if (clickedElement) {
          // Find the parent product section with data-product attribute
          const productSection = clickedElement.closest('[data-product]');
          
          if (productSection) {
            try {
              // Parse the data-product JSON
              const productData = JSON.parse(productSection.getAttribute('data-product') || '{}');
              
              if (productData.id) {
                // Check if this is an add-to-cart button or product link click
                const isAddToCart = clickedElement.classList.contains('add_to_cart_button');
                
                if (isAddToCart) {
                  // Fire add_to_cart event for cart buttons
                  this.trackAddToCart({
                    productId: productData.id.toString(),
                    productSku: productData.sku || undefined,
                    productName: productData.name || undefined,
                    productPrice: productData.price || undefined,
                    quantity: 1
                  });
                  
                  this.log('🛒 Chatbot add-to-cart clicked:', { 
                    productId: productData.id, 
                    productName: productData.name
                  });
                } else {
                  // Fire product_clicked event for product links (Details)
                  this.trackProductClick({
                    productId: productData.id.toString(),
                    productSku: productData.sku || undefined,
                    productName: productData.name || undefined,
                    productPrice: productData.price || undefined
                  });
                  
                  this.log('🖱️ Chatbot product clicked:', { 
                    productId: productData.id, 
                    productName: productData.name,
                    clickType: clickedElement.className 
                  });
                }
              }
            } catch (error) {
              this.log('❌ Failed to parse product data:', error);
            }
          }
        }
      }, true); // Use capture phase
    };

    // Setup click handler on document (for regular DOM)
    setupClickHandler(document);
    
    // Watch for chatbot widget with Shadow DOM
    const observeShadowRoots = () => {
      // Check if chatbot widget shadow root exists
      const widgetRoot = document.getElementById('ai-chat-widget-root');
      if (widgetRoot && widgetRoot.shadowRoot) {
        this.log('✅ Found chatbot Shadow DOM, attaching click handler');
        setupClickHandler(widgetRoot.shadowRoot);
        return true;
      }
      return false;
    };

    // Wait for DOM to be ready before trying to observe
    const initObserver = () => {
      this.log('🔍 Looking for chatbot Shadow DOM...');
      
      // Try immediately
      if (!observeShadowRoots()) {
        // If not found, wait for it to be created
        if (document.body) {
          const observer = new MutationObserver(() => {
            if (observeShadowRoots()) {
              observer.disconnect();
            }
          });
          
          observer.observe(document.body, {
            childList: true,
            subtree: true
          });
          
          this.log('👀 Watching for chatbot widget creation...');
          
          // Stop observing after 10 seconds
          setTimeout(() => {
            observer.disconnect();
            this.log('⏱️ Stopped watching for chatbot (timeout)');
          }, 10000);
        } else {
          this.log('⚠️ document.body not ready, will retry...');
          setTimeout(initObserver, 100);
        }
      }
    };
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initObserver);
    } else {
      initObserver();
    }
    
    this.log('✅ Chatbot product click tracking initialized');
  }

  /**
   * Track page view
   */
  trackPageView(): void {
    const event = this.buildEvent('page_view', {
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrer: document.referrer
    });

    this.apiClient.track(event);
    this.log('Tracked: page_view');
  }

  /**
   * Track product view
   */
  trackProductView(product: ProductData): void {
    const event = this.buildEvent('product_viewed', {
      productId: product.productId,
      productSku: product.productSku,
      productName: product.productName,
      productPrice: product.productPrice,
      productCategory: product.productCategory,
      productUrl: product.productUrl
    });

    this.apiClient.track(event);
    this.log('Tracked: product_viewed', product);
  }

  /**
   * Track chatbot product click
   * Called when user clicks on a product link inside the chatbot
   */
  trackProductClick(product: ProductData): void {
    const event = this.buildEvent('product_clicked', {
      productId: product.productId,
      productSku: product.productSku,
      productName: product.productName,
      productPrice: product.productPrice,
      source: 'chatbot'
    });

    this.apiClient.track(event);
    this.log('Tracked: product_clicked', product);
  }

  /**
   * Track add to cart
   */
  trackAddToCart(product: ProductData): void {
    const event = this.buildEvent('add_to_cart', {
      productId: product.productId,
      productSku: product.productSku,
      productName: product.productName,
      productPrice: product.productPrice,
      quantity: product.quantity || 1
    });

    this.apiClient.track(event);
    this.log('Tracked: add_to_cart', product);
  }

  /**
   * Track view cart
   */
  trackViewCart(cart?: CartData): void {
    const event = this.buildEvent('cart_viewed', {
      cartId: cart?.cartId,
      itemCount: cart?.items?.length || 0,
      cartValue: cart?.total || 0,
      currency: cart?.currency
    });

    this.apiClient.track(event);
    this.log('Tracked: cart_viewed', cart);
  }

  /**
   * Track checkout initiated
   */
  trackCheckout(cart?: CartData): void {
    const event = this.buildEvent('checkout_initiated', {
      cartId: cart?.cartId,
      itemCount: cart?.items?.length || 0,
      cartValue: cart?.total || 0,
      currency: cart?.currency
    });

    this.apiClient.track(event);
    this.log('Tracked: checkout_initiated', cart);
  }

  /**
   * Track purchase
   */
  trackPurchase(order: OrderData): void {
    const event = this.buildEvent('purchase_completed', {
      orderId: order.orderId,
      orderNumber: order.orderNumber,
      orderValue: order.orderValue,
      currency: order.currency,
      tax: order.tax,
      shipping: order.shipping,
      itemCount: order.items?.length || 0,
      items: order.items
    });

    this.apiClient.track(event);
    this.log('Tracked: purchase_completed', order);
  }

  /**
   * Track chat started
   */
  trackChatStarted(metadata?: Record<string, any>): void {
    const event = this.buildEvent('chat_started', metadata);
    
    // Mark session as chatbot-assisted
    this.sessionManager.markAsChatbotAssisted();

    this.apiClient.track(event);
    this.log('Tracked: chat_started');
  }

  /**
   * Track chatbot opened
   */
  trackChatbotOpened(metadata?: Record<string, any>): void {
    const event = this.buildEvent('chatbot_opened', metadata);
    this.apiClient.track(event);
    this.log('Tracked: chatbot_opened');
  }

  /**
   * Track chatbot closed
   */
  trackChatbotClosed(metadata?: Record<string, any>): void {
    const event = this.buildEvent('chatbot_closed', metadata);
    this.apiClient.track(event);
    this.log('Tracked: chatbot_closed');
  }

  /**
   * Track chat message
   */
  trackChatMessage(message: string, isUser: boolean, metadata?: Record<string, any>): void {
    const event = this.buildEvent('chat_message', {
      message,
      isUser,
      ...metadata
    });

    this.apiClient.track(event);
    this.log('Tracked: chat_message');
  }

  /**
   * Track custom event
   */
  track(eventType: EventType, metadata?: Record<string, any>): void {
    const event = this.buildEvent(eventType, metadata);
    this.apiClient.track(event);
    this.log(`Tracked: ${eventType}`, metadata);
  }

  /**
   * Build tracking event with all context
   */
  private buildEvent(eventType: EventType, metadata?: Record<string, any>): TrackingEvent {
    const session = this.sessionManager.getSession()!;
    const device = Fingerprint.getDeviceInfo();

    const event: TrackingEvent = {
      eventId: Fingerprint.generateEventId(),
      eventType,
      timestamp: new Date().toISOString(),
      clientId: this.config.clientId!,
      
      // Session context
      sessionId: session.sessionId,
      visitorId: session.visitorId,
      chatbotAssisted: session.chatbotAssisted,
      
      // Page context
      pageUrl: window.location.href,
      pageTitle: document.title,
      referrer: document.referrer,
      
      // Device context
      deviceType: device.deviceType,
      browser: device.browser,
      browserVersion: device.browserVersion,
      os: device.os,
      screenResolution: device.screenResolution,
      screenWidth: device.screenWidth,
      screenHeight: device.screenHeight,
      language: device.language,
      timezone: device.timezone,
      
      // Attribution
      utmSource: session.utmParams.source,
      utmMedium: session.utmParams.medium,
      utmCampaign: session.utmParams.campaign,
      utmTerm: session.utmParams.term,
      utmContent: session.utmParams.content,
      gclid: session.gclid,
      fbclid: session.fbclid,
      
      // Additional metadata
      data: metadata || {}
    };

    // Log to debug overlay
    if (this.debugOverlay) {
      this.debugOverlay.logEvent({
        id: event.eventId,
        type: eventType,
        timestamp: event.timestamp,
        data: metadata || {},
        status: 'queued'
      });
    }

    return event;
  }

  /**
   * Check for debug param in URL
   */
  private hasDebugParam(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.has('chatbot_debug') || urlParams.get('chatbot_debug') === 'true';
  }

  /**
   * Get debug overlay instance (for external access)
   */
  getDebugOverlay(): DebugOverlay | null {
    return this.debugOverlay;
  }

  /**
   * Flush pending events
   */
  flush(): void {
    this.apiClient.flush();
    this.log('Flushed pending events');
  }

  /**
   * Get current session
   */
  getSession() {
    return this.sessionManager.getSession();
  }

  /**
   * Get cart data from platform
   */
  async getCart(): Promise<CartData | null> {
    return this.adapter.getCart();
  }

  /**
   * Debug logging
   */
  private log(_message: string, _data?: any): void {
    // Browser console output intentionally removed
  }

  /**
   * Cleanup
   */
  destroy(): void {
    if (this.adapter) {
      this.adapter.destroy();
    }
    if (this.debugOverlay) {
      this.debugOverlay.destroy();
      this.debugOverlay = null;
    }
    this.initialized = false;
    this.log('Tracker destroyed');
  }
}

// Global singleton instance
let trackerInstance: ChatbotAnalyticsTracker | null = null;

// Public API
export const ChatbotAnalytics = {
  
  /**
   * Initialize tracker
   */
  init(config: Partial<TrackerConfig>): void {
    if (!trackerInstance) {
      trackerInstance = new ChatbotAnalyticsTracker();
    }
    trackerInstance.init(config);
  },

  /**
   * Track custom event
   */
  track(eventType: EventType, metadata?: Record<string, any>): void {
    if (!trackerInstance) {
      return;
    }
    trackerInstance.track(eventType, metadata);
  },

  /**
   * Track chat started
   */
  trackChatStarted(metadata?: Record<string, any>): void {
    if (!trackerInstance) {
      return;
    }
    trackerInstance.trackChatStarted(metadata);
  },

  /**
   * Track chat message
   */
  trackChatMessage(message: string, isUser: boolean, metadata?: Record<string, any>): void {
    if (!trackerInstance) {
      return;
    }
    trackerInstance.trackChatMessage(message, isUser, metadata);
  },

  /**
   * Flush events
   */
  flush(): void {
    if (trackerInstance) {
      trackerInstance.flush();
    }
  },

  /**
   * Get current session
   */
  getSession() {
    if (!trackerInstance) {
      return null;
    }
    return trackerInstance.getSession();
  },

  /**
   * Get cart
   */
  async getCart(): Promise<CartData | null> {
    if (!trackerInstance) {
      return null;
    }
    return trackerInstance.getCart();
  },

  /**
   * Destroy tracker
   */
  destroy(): void {
    if (trackerInstance) {
      trackerInstance.destroy();
      trackerInstance = null;
    }
  },

  /**
   * Debug overlay access (for manual control)
   * Usage: ChatbotAnalytics._debug.show(), ChatbotAnalytics._debug.exportEvents()
   */
  _debug: {
    show(): void {
      trackerInstance?.getDebugOverlay()?.show();
    },
    hide(): void {
      trackerInstance?.getDebugOverlay()?.hide();
    },
    toggle(): void {
      trackerInstance?.getDebugOverlay()?.toggle();
    },
    exportEvents(): void {
      trackerInstance?.getDebugOverlay()?.exportEvents();
    },
    clearEvents(): void {
      trackerInstance?.getDebugOverlay()?.clearEvents();
    }
  }
};

// Expose to window
declare global {
  interface Window {
    ChatbotAnalytics: typeof ChatbotAnalytics;
  }
}

if (typeof window !== 'undefined') {
  window.ChatbotAnalytics = ChatbotAnalytics;
}

export default ChatbotAnalytics;
